const aiLlms = {
  title: "Large Language Models",
  subtitle: "How LLMs work, what they can and cannot do, and how to get the best results from them.",
  sections: [
    {
      id: "what-are-llms",
      title: "What are Large Language Models?",
      content: [
        "A Large Language Model is a neural network trained on vast amounts of text to predict the next token (word or sub-word) in a sequence. Despite this simple objective, the emergent behaviour is remarkably powerful — these models can write, reason, code, translate, and answer questions [1].",
        "The 'large' in LLM refers to both the model size (billions of parameters) and the training data (trillions of tokens from books, websites, code, and conversations). Scale has been the defining trend — larger models with more data consistently show new capabilities that smaller models lack [2].",
        "Modern LLMs include GPT-4, Claude, Gemini, Llama, and Mistral. While they differ in architecture details and training approaches, they are all transformer-based models that generate text one token at a time."
      ]
    },
    {
      id: "how-they-work",
      title: "How LLMs Generate Text",
      content: [
        "When you send a prompt to an LLM, the model processes all your tokens through its transformer layers, building up a rich representation of the context. Then it predicts a probability distribution over the entire vocabulary for the next token [3].",
        "The model samples from this distribution (with some randomness controlled by temperature), appends the chosen token, and repeats. This autoregressive process continues until the model produces a stop token or hits the maximum length.",
        {
          type: "bullets",
          items: [
            "Temperature: controls randomness — 0 is deterministic (always pick the most likely token), 1 is more creative/varied",
            "Top-p (nucleus sampling): only consider tokens whose cumulative probability exceeds a threshold — filters out unlikely tokens",
            "Context window: the total number of tokens (prompt + response) the model can handle — determines how much information it can consider",
            "Tokenization: text is split into tokens (roughly 3/4 of a word on average) — 'understanding' might become 'under' + 'standing'"
          ]
        }
      ]
    },
    {
      id: "capabilities",
      title: "What LLMs Can Do Well",
      content: [
        "LLMs excel at tasks that involve pattern recognition over text, synthesis of information, and generation of structured content. Their capabilities have expanded significantly with each generation [4].",
        {
          type: "bullets",
          items: [
            "Writing and editing: drafting, summarizing, rewriting, translating, and adapting tone or style",
            "Code generation: writing, debugging, explaining, and refactoring code across dozens of languages",
            "Question answering: synthesizing information from their training data to answer factual and analytical questions",
            "Reasoning: step-by-step logical reasoning, mathematical problem-solving, and analytical thinking",
            "Extraction and transformation: pulling structured data from unstructured text, reformatting, and organizing",
            "Creative tasks: brainstorming, ideation, storytelling, and generating variations on a theme"
          ]
        }
      ]
    },
    {
      id: "limitations",
      title: "What LLMs Cannot Do (or Do Poorly)",
      content: [
        "Understanding the limitations is as important as understanding the capabilities. LLMs fail in predictable ways, and knowing these patterns helps you use them more effectively [5].",
        {
          type: "bullets",
          items: [
            "Hallucination: LLMs can generate plausible-sounding but factually incorrect information — they do not 'know' things, they predict likely text",
            "Real-time information: models have a training cutoff date and do not know about recent events unless given tools",
            "Precise calculation: while improving, LLMs can make arithmetic errors — they predict the answer rather than compute it",
            "Long-term memory: each conversation starts fresh — the model does not remember previous sessions unless explicitly given context",
            "Consistent persona: LLMs can contradict themselves within a conversation, especially in long sessions",
            "Reliable counting: counting characters, words, or items in a list is surprisingly unreliable",
            "Understanding vs pattern matching: whether LLMs truly 'understand' is debated — they excel at pattern matching but may lack deeper comprehension"
          ]
        }
      ]
    },
    {
      id: "prompting",
      title: "Prompt Engineering",
      content: [
        "Prompt engineering is the practice of structuring your input to get better outputs from an LLM. It is not magic — it is mostly about being clear, specific, and giving the model enough context to do good work [6].",
        {
          type: "bullets",
          items: [
            "Be specific: 'Write a Python function that takes a list of integers and returns the second largest' beats 'Write some Python code'",
            "Provide context: tell the model who the audience is, what format you want, and what constraints apply",
            "Use examples (few-shot): showing the model 2-3 examples of input/output pairs dramatically improves consistency",
            "Chain of thought: asking the model to 'think step by step' improves reasoning on complex problems",
            "System prompts: set the model's role, tone, and constraints at the start of the conversation",
            "Iterate: treat prompting as a conversation — refine based on what you get back rather than trying to write the perfect prompt upfront"
          ]
        }
      ]
    },
    {
      id: "models-landscape",
      title: "The Model Landscape",
      content: [
        "The LLM ecosystem has expanded rapidly. Understanding the major players and their trade-offs helps you choose the right tool for each task.",
        {
          type: "bullets",
          items: [
            "OpenAI (GPT-4, GPT-4o): the model that brought LLMs mainstream — strong all-rounder, widely integrated",
            "Anthropic (Claude): focused on safety and helpfulness — excels at long documents, coding, and careful reasoning",
            "Google (Gemini): multimodal from the ground up — handles text, images, video, and code",
            "Meta (Llama): open-weight models that can be run locally — important for privacy and customization",
            "Mistral: efficient open models from a European lab — strong performance relative to size",
            "Open source ecosystem: Hugging Face hosts thousands of models — from tiny specialized models to large general-purpose ones"
          ]
        },
        "The landscape changes quarterly. What matters more than any specific model is understanding what makes a model good for your use case: speed, cost, accuracy, context length, multimodal support, and whether you need to run it locally."
      ]
    }
  ],
  references: [
    { id: 1, text: "Brown, T. et al. — Language Models are Few-Shot Learners (GPT-3 paper), NeurIPS 2020", url: "https://arxiv.org/abs/2005.14165" },
    { id: 2, text: "Kaplan, J. et al. — Scaling Laws for Neural Language Models, 2020", url: "https://arxiv.org/abs/2001.08361" },
    { id: 3, text: "Radford, A. et al. — Language Models are Unsupervised Multitask Learners (GPT-2 paper), 2019" },
    { id: 4, text: "Bubeck, S. et al. — Sparks of Artificial General Intelligence: Early experiments with GPT-4, 2023", url: "https://arxiv.org/abs/2303.12712" },
    { id: 5, text: "Bender, E. et al. — On the Dangers of Stochastic Parrots: Can Language Models Be Too Big?, FAccT 2021" },
    { id: 6, text: "White, J. et al. — A Prompt Pattern Catalog to Enhance Prompt Engineering with ChatGPT, 2023", url: "https://arxiv.org/abs/2302.11382" }
  ]
};

export default aiLlms;
